import { Crosshair, Navigation, Route as RouteIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FieldMissionItem, TrackPoint } from '@groupe-reca/contracts'
import {
  loadGoogleMaps,
  type GoogleMapInstance,
  type GoogleMapsApi,
  type StreetViewPanorama,
} from '@/shared/google/googleMaps'
import { createSuggestedPathLayer, type SuggestedPathLayer } from '@/shared/google/suggestedPathLayer'
import { createTrackLayer, type TrackLayer } from '@/shared/google/trackLayer'
import { centerForAnchor, recenterIfNeeded, type Viewport } from '../../domain/map/mapCamera'
import { splitAt } from '../../domain/map/pathGeometry'
import { civicNumber } from '../../domain/map/civicNumber'
import { speedLabel, type SpeedState } from '../../domain/map/fieldMeasures'
import { guideMissingLabels, navigationToPoint, type GuideAvailability, type Liaison } from '../../domain/map/routeGuide'
import { createRoutePathLayer, type RoutePathLayer } from '../../infrastructure/google/routePathLayer'
import type { StableMap } from '../useStableMap'
import type { SuggestedPathState } from '../useSuggestedPath'
import { bearingDegrees, headingApplied, unwrapAngle, type GeoPoint } from '../../application/tracking/geo'
import type { TrackerState } from '../../application/tracking/PositionTracker'
import { hasCoordinates, itemAddress } from '../../domain/fieldText'
import type { Radii } from '@/features/detection/domain/geometry'
import { createDebugFlagLayer, type DebugFlag, type DebugFlagLayer } from '../../infrastructure/google/debugFlagLayer'
import { createFieldMapLayer, type FieldMapLayer, type MarkerEmphasis } from '../../infrastructure/google/missionMapLayer'

// Repris (adapt) de reca-app-v3 `src/features/markers/presentation/field/FieldMap.tsx` : satellite par défaut,
// glisser / pincer, Satellite / Plan, « Me recentrer ». Après un déplacement manuel, la carte ne se recentre plus
// toute seule. Seules les résidences de cette mission et la tablette sont affichées.

export interface MissionMapProps {
  apiKey: string | undefined
  /** ID de carte vectorielle : rotation native (`setHeading`). Absent ou refusé : rotation CSS du cadre. */
  mapId?: string | undefined
  /** Diagnostic : mode de rotation appliqué et suivi du tracteur (nord en haut hors suivi). */
  onRotationChange?: (info: { rotation: RotationMode; following: boolean }) => void
  items: FieldMissionItem[]
  selectedId: string | null
  /** Carte masquée (fiche ouverte) mais jamais démontée : zoom, centre et type sont conservés. */
  hidden: boolean
  operator: TrackerState
  /** Tronçons de la trace enregistrée; `null` : option « Afficher le trajet » désactivée. */
  track: TrackPoint[][] | null
  /** Trajet suggéré par la rue vers la prochaine résidence (option « Itinéraire »). */
  suggested?: SuggestedPathState
  /** Bouton « Itinéraire » : absent sans jeton Mapbox. */
  onToggleSuggested?: () => void
  suggestedOn?: boolean
  /** Bouton « Trajet » (trace GPS enregistrée), sous le zoom. */
  onToggleTrack?: () => void
  trackOn?: boolean
  /** Maison montrée par « Street View » : la résidence mise en avant. */
  streetTarget?: { id: string; point: GeoPoint; address: string } | null
  /** État du moteur de détection; `null` : détection désactivée. */
  detection: MapDetection | null
  /** Calibration temporaire : drapeaux et rayons tracés; `null` : masqués. */
  debugFlags?: { flags: DebugFlag[]; radii: Radii } | null
  onOpen: (itemId: string) => void
  /** Représentation stabilisée (symbole, orientation, progression), distincte de la position réelle. */
  stable: StableMap
  /** Parcours validé figé dans la mission, ou raison de son absence (repli explicite). */
  guide?: GuideAvailability
  routeColor?: string | null
  civicSize?: 'normal' | 'large'
  /** Liaison proposée quand plus rien n'est à faire (route suivante, retour au dépôt). */
  liaison?: Liaison | null
}

/** Vitesse GPS filtrée, avec sa propre horloge : « — » dès que la mesure vieillit, jamais 0 par défaut. */
function SpeedBadge({ speed }: { speed: SpeedState }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now())
    }, 2_000)
    return () => {
      clearInterval(timer)
    }
  }, [])
  return (
    <div className="field-map__speed" aria-label="Vitesse GPS">
      <strong>{speedLabel(speed, Math.max(now, speed.at ?? 0))}</strong> km/h
    </div>
  )
}

export interface MapDetection {
  activeId: string | null
  /** Libellé de l'active (« verrouillée », « en cours »). */
  activeChip: string
  approachIds: string[]
  candidateIds: string[]
  /** Précision annoncée quand elle bloque la détection (halo et avis « position incertaine »). */
  uncertaintyM: number | null
}

function emphasisOf(itemId: string, detection: MapDetection | null): { emphasis: MarkerEmphasis; chip: string | null } {
  if (!detection) return { emphasis: null, chip: null }
  if (detection.candidateIds.includes(itemId)) return { emphasis: 'CANDIDATE', chip: '?' }
  if (detection.activeId === itemId) return { emphasis: 'ACTIVE', chip: detection.activeChip }
  if (detection.approachIds.includes(itemId)) return { emphasis: 'APPROACH', chip: 'en approche' }
  return { emphasis: null, chip: null }
}

type MapStatus = 'loading' | 'ready' | 'unavailable'
type MapView = 'satellite' | 'roadmap' | 'tilted' | 'street'
/** Rayon de recherche d'une vue de rue autour de la maison. */
const streetRadiusM = 60
/** `pending` : rendu Google pas encore connu, on tourne le cadre en attendant. */
export type RotationMode = 'pending' | 'native' | 'css'

const defaultCenter = { lat: 45.7783, lng: -74.0035 }

/**
 * Zoom de suivi : environ 5 maisons par côté de rue (≈ 100 m de rue visibles). La carte satellite est
 * en tuiles : le zoom est entier, 19 ≈ 135 m de haut sur le panneau de la tablette.
 */
export const followZoom = 19
/** Au-delà de ce déplacement (px) un doigt qui glisse quitte le suivi. */
const dragExitPx = 14
/** Vue inclinée : perspective native modérée, entrées encore lisibles sur la tablette. */
const tiltDegrees = 45
/** Marges recouvertes par les commandes de la carte (px) : zone utile du recentrage. */
const overlayInsets = { top: 72, right: 132, bottom: 104, left: 16 }
const missingGuide: GuideAvailability = { kind: 'MISSING', reason: 'ABSENT' }

export function MissionMap({
  apiKey,
  mapId,
  onRotationChange,
  items,
  selectedId,
  hidden,
  operator,
  track,
  suggested = { status: 'off' },
  onToggleSuggested,
  suggestedOn = false,
  onToggleTrack,
  trackOn = false,
  streetTarget = null,
  detection,
  debugFlags = null,
  onOpen,
  stable,
  guide = missingGuide,
  routeColor = null,
  civicSize = 'normal',
  liaison = null,
}: MissionMapProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<GoogleMapInstance | null>(null)
  const apiRef = useRef<GoogleMapsApi | null>(null)
  const layerRef = useRef<FieldMapLayer | null>(null)
  const trackRef = useRef<TrackLayer | null>(null)
  const suggestedRef = useRef<SuggestedPathLayer | null>(null)
  const debugRef = useRef<DebugFlagLayer | null>(null)
  const pathRef = useRef<RoutePathLayer | null>(null)
  const anchoredRef = useRef(false)
  const onOpenRef = useRef(onOpen)
  const fittedRef = useRef(false)
  const zoomedForFollowRef = useRef(false)
  const angleRef = useRef(0)
  const [status, setStatus] = useState<MapStatus>(apiKey ? 'loading' : 'unavailable')
  const [attempt, setAttempt] = useState(0)
  const [mapType, setMapType] = useState<MapView>('satellite')
  const street = mapType === 'street'
  const streetRef = useRef<HTMLDivElement | null>(null)
  const panoramaRef = useRef<StreetViewPanorama | null>(null)
  const [streetResult, setStreetResult] = useState<{ id: string; found: boolean } | null>(null)
  const [follow, setFollow] = useState(true)
  const [rotor, setRotor] = useState({ size: 0 })
  const [angle, setAngle] = useState(0)
  const [rotation, setRotation] = useState<RotationMode>(mapId ? 'pending' : 'css')

  useEffect(() => {
    onOpenRef.current = onOpen
  }, [onOpen])

  const position = operator.position
  const hasFix = position !== null && (operator.status === 'ok' || operator.status === 'stale')
  const following = follow && hasFix
  const uncertaintyM = detection?.uncertaintyM ?? null

  const markers = useMemo(
    () =>
      items.flatMap((item) =>
        item.status !== 'COMPLETED' && hasCoordinates(item.snapshot)
          ? [
              {
                id: item.id,
                position: { lat: item.snapshot.latitude ?? 0, lng: item.snapshot.longitude ?? 0 },
                address: itemAddress(item.snapshot),
                name: item.snapshot.clientName ?? '',
                status: item.status,
                civic: civicNumber(item.snapshot.addressLine1),
                order: item.order,
                ...emphasisOf(item.id, detection),
              },
            ]
          : [],
      ),
    [items, detection],
  )

  // Le cadre qui tourne couvre la diagonale du panneau : aucun coin vide à n'importe quel angle.
  useEffect(() => {
    const panel = panelRef.current
    if (!panel || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      setRotor({ size: Math.ceil(Math.hypot(panel.clientWidth, panel.clientHeight)) })
    })
    observer.observe(panel)
    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!apiKey || !containerRef.current) return
    let cancelled = false
    let renderingListener: { remove: () => void } | null = null
    const element = containerRef.current
    loadGoogleMaps(apiKey)
      .then((api) => {
        if (cancelled) return
        apiRef.current = api
        const map = new api.Map(element, {
          ...(mapId ? { mapId, headingInteractionEnabled: false, tiltInteractionEnabled: false } : {}),
          center: defaultCenter,
          zoom: 15,
          mapTypeId: 'satellite',
          // Pas de glisser natif quand la carte est tournée (les gestes seraient décalés) : voir suivi.
          gestureHandling: 'none',
          clickableIcons: false,
          disableDefaultUI: true,
          keyboardShortcuts: false,
          draggableCursor: 'grab',
        })
        mapRef.current = map
        setRotation(mapId ? 'pending' : 'css')
        renderingListener = map.addListener('renderingtype_changed', () => {
          const rendering = map.getRenderingType()
          if (rendering === 'VECTOR') setRotation((mode) => (mode === 'pending' ? 'native' : mode))
          else if (rendering === 'RASTER') setRotation('css')
        })
        layerRef.current = createFieldMapLayer(api, map, (itemId) => {
          onOpenRef.current(itemId)
        })
        trackRef.current = createTrackLayer(api, map)
        suggestedRef.current = createSuggestedPathLayer(api, map)
        pathRef.current = createRoutePathLayer(api, map)
        debugRef.current = createDebugFlagLayer(api, map)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable')
      })
    return () => {
      cancelled = true
      renderingListener?.remove()
      layerRef.current?.destroy()
      layerRef.current = null
      trackRef.current?.destroy()
      trackRef.current = null
      suggestedRef.current?.destroy()
      suggestedRef.current = null
      pathRef.current?.destroy()
      pathRef.current = null
      debugRef.current?.destroy()
      debugRef.current = null
      mapRef.current = null
      fittedRef.current = false
      zoomedForFollowRef.current = false
    }
  }, [apiKey, mapId, attempt])

  // Cap affiché : orientation stabilisée (tronçon prévu, figée pendant une intervention), jamais le cap instantané du
  // GPS; angle « déroulé » (jamais de tour complet) ; nord en haut hors suivi.
  const targetHeading = following ? stable.camera.heading : null
  useEffect(() => {
    if (targetHeading === null) {
      if (!following) {
        angleRef.current = unwrapAngle(angleRef.current, 0)
        setAngle(angleRef.current)
      }
      return
    }
    angleRef.current = unwrapAngle(angleRef.current, targetHeading)
    setAngle(angleRef.current)
  }, [targetHeading, following])

  // Rotation native : si Google n'applique pas le cap (ex. imagerie satellite matricielle), retour au cadre CSS.
  useEffect(() => {
    const map = mapRef.current
    if (rotation !== 'native' || status !== 'ready' || !map) return
    map.setHeading(angle)
    if (!headingApplied(map.getHeading(), angle)) {
      map.setHeading(0)
      setRotation('css')
    }
  }, [rotation, angle, status])
  const cssAngle = rotation === 'native' ? 0 : angle
  useEffect(() => {
    onRotationChange?.({ rotation, following })
  }, [onRotationChange, rotation, following])

  useEffect(() => {
    // Le suivi redonne la main au doigt : glisser natif seulement hors suivi.
    mapRef.current?.setOptions({ gestureHandling: following ? 'none' : 'greedy' })
  }, [following, status])

  // Symbole affiché : représentation stabilisée (la position réelle n'est jamais déplacée). Hors suivi, la position
  // réelle est montrée telle quelle.
  const symbol = following ? (stable.camera.symbol ?? position) : hasFix ? position : null
  const symbolLabel = following && stable.camera.stabilized ? 'Vous · vue stabilisée' : 'Vous'
  useEffect(() => {
    layerRef.current?.update(markers, selectedId, symbol, following ? cssAngle : 0, uncertaintyM, symbolLabel)
  }, [markers, selectedId, symbol, status, following, cssAngle, uncertaintyM, symbolLabel])

  // Parcours prévu : restant au trait plein, parcouru (progression confirmée seulement) atténué.
  const polyline = guide.kind === 'READY' ? guide.polyline : null
  const travelledCut = Math.round(stable.travelled / 10) * 10
  const pathColor = routeColor ?? '#3B82F6'
  useEffect(() => {
    if (!polyline) {
      pathRef.current?.update(null)
      return
    }
    const parts = splitAt(polyline, travelledCut)
    pathRef.current?.update({
      remaining: [parts.remaining],
      travelled: parts.travelled.length > 1 ? [parts.travelled] : [],
      color: pathColor,
    })
  }, [polyline, travelledCut, pathColor, status])

  useEffect(() => {
    trackRef.current?.update(track ?? [])
  }, [track, status])

  useEffect(() => {
    layerRef.current?.setCivicSize(civicSize)
  }, [civicSize, status])

  const suggestedPath = suggested.status === 'ready' ? suggested.route.path : null
  useEffect(() => {
    suggestedRef.current?.update(suggestedPath)
  }, [suggestedPath, status])

  useEffect(() => {
    if (debugFlags) debugRef.current?.update(debugFlags.flags, debugFlags.radii)
    else debugRef.current?.update([], { captureRadiusM: 0, workRadiusM: 0, exitDistanceM: 0 })
  }, [debugFlags, status])

  // Vue initiale, une seule fois et seulement quand le cadre a une taille (sinon Google reporte le
  // cadrage et il écraserait le zoom de suivi) : suivi à ~5 maisons par côté si la position est connue,
  // sinon cadrage sur les adresses.
  const sized = rotor.size > 0
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map || !sized) return
    if (following) {
      if (!zoomedForFollowRef.current) {
        zoomedForFollowRef.current = true
        fittedRef.current = true
        map.setZoom(followZoom)
        map.setCenter(position)
      }
      return
    }
    if (fittedRef.current || markers.length === 0 || hasFix) return
    fittedRef.current = true
    const lats = markers.map((marker) => marker.position.lat)
    const lngs = markers.map((marker) => marker.position.lng)
    map.fitBounds(
      {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      },
      60,
    )
  }, [status, sized, following, hasFix, markers, position])

  // Suivi : le symbole avance librement; la carte ne se recentre (progressivement) que lorsqu'il approche une marge de
  // la zone utile, en laissant plus de parcours devant. À la reprise du suivi, recentrage explicite.
  const symbolLat = symbol?.lat ?? null
  const symbolLng = symbol?.lng ?? null
  useEffect(() => {
    const map = mapRef.current
    const panel = panelRef.current
    if (!following) anchoredRef.current = false
    if (status !== 'ready' || !map || !panel || !following || !sized || symbolLat === null || symbolLng === null) return
    const current = map.getCenter()
    const zoom = map.getZoom() ?? followZoom
    const center = current ? { lat: current.lat(), lng: current.lng() } : { lat: symbolLat, lng: symbolLng }
    const viewport: Viewport = {
      width: panel.clientWidth,
      height: panel.clientHeight,
      useful: {
        left: overlayInsets.left,
        top: overlayInsets.top,
        right: panel.clientWidth - overlayInsets.right,
        bottom: panel.clientHeight - overlayInsets.bottom,
      },
    }
    const point = { lat: symbolLat, lng: symbolLng }
    if (!anchoredRef.current) {
      anchoredRef.current = true
      map.setCenter(centerForAnchor(center, point, angle, zoom, viewport))
      return
    }
    const next = recenterIfNeeded(center, point, angle, zoom, viewport)
    if (next) map.panTo(next)
  }, [status, sized, following, symbolLat, symbolLng, angle])

  // Vue inclinée : perspective native (carte vectorielle). Rendu matriciel : repli 2D stable, annoncé.
  const tilted = mapType === 'tilted'
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map) return
    map.setTilt(tilted && rotation === 'native' ? tiltDegrees : 0)
  }, [tilted, rotation, status])

  useEffect(() => {
    if (!hidden && apiRef.current && mapRef.current) apiRef.current.event.trigger(mapRef.current, 'resize')
  }, [hidden, rotor.size])

  // Gestes tactiles : pincer ajuste le zoom (toujours possible), glisser quitte le suivi.
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const pinchBase = useRef<number | null>(null)
  const dragStart = useRef<{ x: number; y: number } | null>(null)

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    // En Street View, les gestes appartiennent au panorama (ni zoom de carte ni sortie du suivi).
    if (street) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pointers.current.size === 1) dragStart.current = { x: event.clientX, y: event.clientY }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      pinchBase.current = a && b ? Math.hypot(a.x - b.x, a.y - b.y) : null
      dragStart.current = null
    }
  }, [street])

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!pointers.current.has(event.pointerId)) return
      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
      const map = mapRef.current
      if (!map) return
      if (pointers.current.size === 2 && pinchBase.current) {
        const [a, b] = [...pointers.current.values()]
        if (!a || !b) return
        const ratio = Math.hypot(a.x - b.x, a.y - b.y) / pinchBase.current
        if (ratio > 1.5 || ratio < 0.67) {
          map.setZoom((map.getZoom() ?? followZoom) + (ratio > 1 ? 1 : -1))
          pinchBase.current = Math.hypot(a.x - b.x, a.y - b.y)
        }
      } else if (pointers.current.size === 1 && dragStart.current && following) {
        if (
          Math.hypot(event.clientX - dragStart.current.x, event.clientY - dragStart.current.y) > dragExitPx
        ) {
          setFollow(false)
          dragStart.current = null
        }
      }
    },
    [following],
  )

  const onPointerEnd = useCallback((event: React.PointerEvent) => {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) pinchBase.current = null
    if (pointers.current.size === 0) dragStart.current = null
  }, [])

  function changeType(next: MapView) {
    setMapType(next)
    if (next === 'tilted') mapRef.current?.setMapTypeId('roadmap')
    else if (next !== 'street') mapRef.current?.setMapTypeId(next)
  }

  // Street View : vue la plus proche de la maison (≤ 60 m), tournée vers elle. Panorama créé une fois, à la demande.
  const streetId = streetTarget?.id ?? null
  const streetLat = streetTarget?.point.lat ?? null
  const streetLng = streetTarget?.point.lng ?? null
  useEffect(() => {
    const api = apiRef.current
    const element = streetRef.current
    if (!street || status !== 'ready' || !api || !element || streetId === null || streetLat === null || streetLng === null) return
    let cancelled = false
    const house = { lat: streetLat, lng: streetLng }
    new api.StreetViewService()
      .getPanorama({
        location: house,
        radius: streetRadiusM,
        source: api.StreetViewSource.OUTDOOR,
        preference: api.StreetViewPreference.NEAREST,
      })
      .then(({ data }) => {
        if (cancelled) return
        const pano = data.location?.pano
        const at = data.location?.latLng
        if (!pano || !at) {
          setStreetResult({ id: streetId, found: false })
          return
        }
        panoramaRef.current ??= new api.StreetViewPanorama(element, {
          disableDefaultUI: true,
          clickToGo: true,
          linksControl: true,
          showRoadLabels: false,
          motionTracking: false,
        })
        panoramaRef.current.setPano(pano)
        panoramaRef.current.setPov({ heading: bearingDegrees({ lat: at.lat(), lng: at.lng() }, house), pitch: 0 })
        panoramaRef.current.setVisible(true)
        setStreetResult({ id: streetId, found: true })
      })
      .catch(() => {
        if (!cancelled) setStreetResult({ id: streetId, found: false })
      })
    return () => {
      cancelled = true
    }
  }, [street, status, streetId, streetLat, streetLng])

  useEffect(() => {
    if (street && apiRef.current && panoramaRef.current) apiRef.current.event.trigger(panoramaRef.current, 'resize')
  }, [street, hidden])

  const streetMessage = !street
    ? null
    : !streetTarget
      ? 'Aucune résidence mise en avant : pas de maison à montrer.'
      : streetResult?.id !== streetTarget.id
        ? 'Chargement de la vue de rue…'
        : streetResult.found
          ? null
          : 'Aucune vue de rue près de cette maison.'

  function zoomBy(delta: number) {
    const map = mapRef.current
    if (map) map.setZoom((map.getZoom() ?? followZoom) + delta)
  }

  function toggleFollow() {
    if (!hasFix) return
    setFollow((value) => !value)
  }

  const followLabel =
    operator.status === 'pending' || operator.status === 'idle'
      ? 'Localisation…'
      : !hasFix
        ? 'Position indisponible'
        : following
          ? 'Suivi du tracteur'
          : 'Recentrer sur le tracteur'

  return (
    <div
      className="field-map"
      hidden={hidden}
      ref={panelRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      {status === 'unavailable' ? (
        <div className="field-map__unavailable" role="alert">
          <div className="field-map__unavailable-card">
            <strong>Carte indisponible</strong>
            <p>
              Google Maps n’a pas pu se charger. La liste, les fiches et la confirmation du travail restent
              utilisables.
            </p>
            {apiKey ? (
              <button
                type="button"
                className="field-button field-button--primary"
                onClick={() => {
                  setStatus('loading')
                  setAttempt((value) => value + 1)
                }}
              >
                Recharger la carte
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <div
            className="field-map__rotor"
            style={{
              width: rotor.size,
              height: rotor.size,
              marginLeft: -rotor.size / 2,
              marginTop: -rotor.size / 2,
              transform: `rotate(${String(-cssAngle)}deg)`,
            }}
          >
            <div ref={containerRef} className="field-map__canvas" />
          </div>
          {/* Google impose `position: relative` à l'élément du panorama : le cadre absolu l'enveloppe. */}
          <div className="field-map__street" hidden={!street} aria-label={streetTarget ? `Vue de rue du ${streetTarget.address}` : 'Vue de rue'}>
            <div ref={streetRef} className="field-map__street-pano" />
          </div>
          {streetMessage ? (
            <div className="field-map__street-note" role="status">
              {streetMessage}
            </div>
          ) : null}
          {street && streetTarget && streetResult?.id === streetTarget.id && streetResult.found ? (
            <div className="field-map__street-label">{streetTarget.address} · l’image peut dater de plusieurs années</div>
          ) : null}
          <div className="field-map__types">
            <button
              type="button"
              aria-pressed={mapType === 'satellite'}
              onClick={() => {
                changeType('satellite')
              }}
            >
              Satellite
            </button>
            <button
              type="button"
              aria-pressed={mapType === 'roadmap'}
              onClick={() => {
                changeType('roadmap')
              }}
            >
              Routière
            </button>
            <button
              type="button"
              aria-pressed={tilted}
              onClick={() => {
                changeType('tilted')
              }}
            >
              Inclinée
            </button>
            <button
              type="button"
              aria-pressed={street}
              onClick={() => {
                changeType('street')
              }}
            >
              Street View
            </button>
          </div>
          {following && !street && stable.camera.stabilized ? (
            <div className="field-map__stable" role="status">
              {stable.camera.mode === 'PAUSED' ? 'Pause · vue figée' : 'Travail en cours · vue stabilisée'}
            </div>
          ) : null}
          {!street ? <SpeedBadge speed={stable.speed} /> : null}
          {liaison && !street ? (
            <div className="field-map__liaison" role="note">
              <strong>Liaison · {liaison.label}</strong>
              <a
                className="field-button field-button--primary"
                href={navigationToPoint(liaison.point)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation aria-hidden="true" /> M’y rendre
              </a>
            </div>
          ) : null}
          {tilted && rotation === 'css' ? (
            <div className="field-map__street-note" role="status">
              Perspective indisponible sur cet appareil : vue routière 2D stable.
            </div>
          ) : null}
          {operator.status === 'denied' ? (
            <div className="field-map__gps" role="status">
              <strong>GPS refusé.</strong> Votre position n’est pas affichée ni enregistrée. Fiches et confirmations
              restent disponibles.
            </div>
          ) : null}
          {uncertaintyM !== null ? (
            <div className="field-map__gps field-map__gps--warn" role="status">
              Position incertaine : aucune adresse n’est déduite. Zone affichée = précision annoncée (± {Math.round(uncertaintyM)} m).
            </div>
          ) : null}
          <ul className="field-map__key" aria-label="Légende de la carte" hidden={street}>
            <li>
              <span className="field-map__dot field-map__dot--todo" /> À faire
            </li>
            {detection ? (
              <li>
                <span className="field-map__dot field-map__dot--active" /> Active
              </li>
            ) : null}
            <li>
              <span className="field-map__dot field-map__dot--problem">!</span> Problème
            </li>
            <li>Terminées masquées</li>
            {guide.kind === 'READY' ? (
              <li>
                <span className="field-map__line field-map__line--path" style={{ background: pathColor }} /> Parcours
                prévu (bureau) · version {guide.path.version}
              </li>
            ) : (
              <li>{guideMissingLabels[guide.reason]}</li>
            )}
            {track ? <li>Trace GPS enregistrée, pas un itinéraire calculé</li> : null}
            {suggested.status === 'ready' ? (
              <li>
                <span className="field-map__line field-map__line--suggested" /> Itinéraire suggéré ·{' '}
                {suggested.route.distanceMeters < 1000
                  ? `${String(suggested.route.distanceMeters)} m`
                  : `${(suggested.route.distanceMeters / 1000).toFixed(1).replace('.', ',')} km`}{' '}
                · libre à vous
              </li>
            ) : suggested.status === 'loading' ? (
              <li>Itinéraire suggéré : calcul…</li>
            ) : suggested.status === 'unavailable' ? (
              <li>Itinéraire suggéré indisponible</li>
            ) : null}
          </ul>
          <div className="field-map__controls" hidden={street}>
            <div className="field-map__zoom">
              <button
                type="button"
                aria-label="Zoom avant"
                onClick={() => {
                  zoomBy(1)
                }}
              >
                +
              </button>
              <button
                type="button"
                aria-label="Zoom arrière"
                onClick={() => {
                  zoomBy(-1)
                }}
              >
                −
              </button>
            </div>
            {onToggleTrack ? (
              <button
                type="button"
                className="field-map__recenter field-map__track"
                aria-pressed={trackOn}
                aria-label="Afficher le trajet"
                onClick={onToggleTrack}
              >
                <RouteIcon aria-hidden="true" /> Trajet
              </button>
            ) : null}
            {onToggleSuggested ? (
              <button
                type="button"
                className="field-map__recenter field-map__suggest"
                aria-pressed={suggestedOn}
                aria-label="Itinéraire suggéré par la rue"
                onClick={onToggleSuggested}
              >
                <Navigation aria-hidden="true" /> Itinéraire
              </button>
            ) : null}
            <button
              type="button"
              className="field-map__recenter"
              aria-pressed={following}
              aria-disabled={!hasFix}
              onClick={toggleFollow}
            >
              <Crosshair aria-hidden="true" /> {followLabel}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
